"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContextType {
  theme: Theme;
  actualTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  systemPreference: "light" | "dark";
  isHealthcareOptimized: boolean;
  setHealthcareOptimized: (optimized: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export function ThemeProvider({ children, defaultTheme = "system", storageKey = "lyfe-theme" }: ThemeProviderProps) {
  // Initialize with default values on server, hydrate from localStorage on client
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [isHydrated, setIsHydrated] = useState(false);

  const [systemPreference, setSystemPreference] = useState<"light" | "dark">("light");
  const [isHealthcareOptimized, setIsHealthcareOptimized] = useState(true); // Default to healthcare optimization

  // Hydrate state from localStorage after mount (client-side only)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem(storageKey) as Theme;
      if (savedTheme) {
        setTheme(savedTheme);
      }
      const savedOptimized = localStorage.getItem("lyfe-healthcare-optimized");
      if (savedOptimized !== null) {
        setIsHealthcareOptimized(savedOptimized === "true");
      }
      setIsHydrated(true);
    }
  }, [storageKey]);

  // Get actual theme (resolve 'system' to actual preference)
  const actualTheme = theme === "system" ? systemPreference : theme;

  // Detect system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPreference(e.matches ? "dark" : "light");
    };

    // Set initial value
    setSystemPreference(mediaQuery.matches ? "dark" : "light");

    // Listen for changes
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;

    // Remove existing theme classes
    root.classList.remove("light", "dark");

    // Add current theme class
    root.classList.add(actualTheme);

    // Add healthcare optimization class if enabled
    if (isHealthcareOptimized) {
      root.classList.add("healthcare-optimized");
    } else {
      root.classList.remove("healthcare-optimized");
    }

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", actualTheme === "dark" ? "#0f172a" : "#ffffff");
    }
  }, [actualTheme, isHealthcareOptimized]);

  // Save theme preference to localStorage
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, theme);
    }
  }, [theme, storageKey]);

  // Save healthcare optimization preference
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("lyfe-healthcare-optimized", isHealthcareOptimized.toString());
    }
  }, [isHealthcareOptimized]);

  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  const toggleTheme = () => {
    if (theme === "light") {
      setTheme("dark");
    } else if (theme === "dark") {
      setTheme("light");
    } else {
      // If system, toggle to opposite of current system preference
      setTheme(systemPreference === "light" ? "dark" : "light");
    }
  };

  const value: ThemeContextType = {
    theme,
    actualTheme,
    setTheme: handleSetTheme,
    toggleTheme,
    systemPreference,
    isHealthcareOptimized,
    setHealthcareOptimized: (optimized) => setIsHealthcareOptimized(optimized),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
};

// Healthcare-specific theme utilities
export const useHealthcareTheme = () => {
  const theme = useTheme();

  return {
    ...theme,
    isNightShift: () => {
      const hour = new Date().getHours();
      return hour >= 19 || hour <= 7; // 7 PM to 7 AM
    },
    suggestThemeForShift: () => {
      const hour = new Date().getHours();
      if (hour >= 19 || hour <= 7) {
        return "dark"; // Night shift
      } else {
        return "light"; // Day shift
      }
    },
    isEmergencyMode: () => {
      // This could be connected to actual emergency alerts in the future
      return false;
    },
  };
};
